import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Bed, Users, DollarSign, Clock, TrendingUp, UtensilsCrossed, ArrowUpRight, ExternalLink, BarChart3, Wallet, CreditCard, Banknote, Smartphone, ArrowDownRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PurchaseAnalytics from '@/components/PurchaseAnalytics';
import { useUser } from '@/utils/userHelper';

export default function Dashboard({ user: propUser, isTab, tabId }) {
  const user = useUser(propUser);
  const [stats, setStats] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
    loadPaymentSummary();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentSummary = async () => {
    try {
      const response = await api.get('/dashboard/payment-summary');
      setPaymentSummary(response.data);
    } catch (error) {
      console.error('Failed to load payment summary:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const openPOSPopup = () => {
    const width = 1400;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    window.open(
      '/restaurant?popup=true',
      'BIZIONIX HMS POS',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-4 border-orange-500 border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Rooms',
      value: stats?.total_rooms || 0,
      icon: Bed,
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Occupied',
      value: stats?.occupied_rooms || 0,
      icon: Users,
      gradient: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      title: 'Available',
      value: stats?.available_rooms || 0,
      icon: Bed,
      gradient: 'from-amber-500 to-amber-600',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
    {
      title: 'Occupancy',
      value: `${stats?.occupancy_rate?.toFixed(1) || 0}%`,
      icon: TrendingUp,
      gradient: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
  ];

  const revenueCards = [
    {
      title: "Today's Revenue",
      value: `₹${stats?.today_revenue?.toLocaleString() || 0}`,
      icon: DollarSign,
      trend: '+12%',
      trendUp: true,
    },
    {
      title: 'Total Revenue',
      value: `₹${stats?.total_revenue?.toLocaleString() || 0}`,
      icon: DollarSign,
      trend: '+8%',
      trendUp: true,
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-gray-800" style={{ fontFamily: 'Manrope' }}>
            Welcome back, <span className="text-gradient-warm">{user.name}</span>
          </h1>
          <p className="text-gray-500 mt-2">Here&apos;s what&apos;s happening with your hotel today</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 rounded-xl bg-white border border-orange-100 shadow-sm">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Active Bookings</p>
            <p className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
              {stats?.active_bookings || 0}
            </p>
          </div>
          <div className="px-4 py-2 rounded-xl bg-white border border-orange-100 shadow-sm">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Pending Orders</p>
            <p className="text-2xl font-bold text-orange-600" style={{ fontFamily: 'JetBrains Mono' }}>
              {stats?.pending_orders || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Dashboard Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-orange-100 shadow-sm">
          <TabsTrigger value="overview" className="data-[state=active]:bg-orange-100" data-testid="dashboard-tab-overview">
            <Bed className="h-4 w-4 mr-2" />
            Hotel Overview
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-orange-100" data-testid="dashboard-tab-analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics & Insights
          </TabsTrigger>
        </TabsList>

        {/* Hotel Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index} 
              className="bg-white border-orange-100 hover:border-orange-200 card-hover overflow-hidden shadow-sm"
              data-testid={`stat-card-${index}`}
            >
              <CardContent className="p-5 relative">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-2">
                      {stat.title}
                    </p>
                    <p className="text-3xl font-bold tracking-tight text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`h-10 w-10 rounded-xl ${stat.iconBg} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${stat.iconColor}`} strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Revenue & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Cards */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          {revenueCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card 
                key={index}
                className="bg-white border-orange-100 overflow-hidden shadow-sm"
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Icon className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${card.trendUp ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      <ArrowUpRight className="h-3 w-3" />
                      {card.trend}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mb-1">{card.title}</p>
                  <p className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                    {card.value}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card className="bg-white border-orange-100 shadow-sm" data-testid="quick-actions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-gray-800" style={{ fontFamily: 'Manrope' }}>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <button 
              onClick={() => navigate('/admin/rooms')}
              className="w-full p-4 rounded-xl bg-orange-50 border border-orange-100 hover:border-orange-300 hover:bg-orange-100 transition-all duration-200 text-left group"
              data-testid="quick-action-rooms"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Bed className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Manage Rooms</p>
                    <p className="text-xs text-gray-500">Add or update rooms</p>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
              </div>
            </button>

            <button 
              onClick={() => navigate('/admin/bookings')}
              className="w-full p-4 rounded-xl bg-purple-50 border border-purple-100 hover:border-purple-300 hover:bg-purple-100 transition-all duration-200 text-left group"
              data-testid="quick-action-bookings"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">New Booking</p>
                    <p className="text-xs text-gray-500">Create reservation</p>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-purple-500 transition-colors" />
              </div>
            </button>

            <button 
              onClick={openPOSPopup}
              className="w-full p-4 rounded-xl bg-gradient-to-r from-orange-100 to-purple-100 border border-orange-200 hover:border-orange-300 transition-all duration-200 text-left group"
              data-testid="quick-action-pos"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-gradient-orange flex items-center justify-center">
                    <UtensilsCrossed className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Restaurant POS</p>
                    <p className="text-xs text-orange-600">Opens in new window</p>
                  </div>
                </div>
                <ExternalLink className="h-4 w-4 text-orange-500" />
              </div>
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Payment Summary Widget */}
      <Card className="bg-white border-teal-100 shadow-sm" data-testid="payment-summary-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-gray-800 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Wallet className="h-5 w-5 text-teal-600" />
            Today&apos;s Payments
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPayments ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
            </div>
          ) : paymentSummary ? (
            <div className="space-y-4">
              {/* Total and Trend */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border border-teal-100">
                <div>
                  <p className="text-sm text-gray-500">Total Received</p>
                  <p className="text-3xl font-bold text-teal-600" style={{ fontFamily: 'JetBrains Mono' }}>
                    ₹{paymentSummary.today_total?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {paymentSummary.payment_count || 0} transaction(s)
                  </p>
                </div>
                <div className={`flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium ${
                  paymentSummary.trend_up 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {paymentSummary.trend_up ? (
                    <ArrowUpRight className="h-4 w-4" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4" />
                  )}
                  {paymentSummary.change_percent > 0 ? '+' : ''}{paymentSummary.change_percent}%
                </div>
              </div>

              {/* Payment Methods Breakdown */}
              {paymentSummary.by_method && Object.keys(paymentSummary.by_method).length > 0 ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.entries(paymentSummary.by_method).map(([method, amount]) => {
                    const methodConfig = {
                      cash: { icon: Banknote, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100', label: 'Cash' },
                      card: { icon: CreditCard, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100', label: 'Card' },
                      upi: { icon: Smartphone, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-100', label: 'UPI' },
                      bank_transfer: { icon: DollarSign, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', label: 'Bank Transfer' },
                      cheque: { icon: DollarSign, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-100', label: 'Cheque' },
                    };
                    const config = methodConfig[method] || { icon: Wallet, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-100', label: method };
                    const Icon = config.icon;
                    
                    return (
                      <div key={method} className={`p-3 rounded-lg ${config.bg} border ${config.border}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-4 w-4 ${config.color}`} />
                          <span className="text-xs text-gray-600 capitalize">{config.label}</span>
                        </div>
                        <p className={`text-lg font-bold ${config.color}`} style={{ fontFamily: 'JetBrains Mono' }}>
                          ₹{amount?.toLocaleString() || 0}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 text-sm py-4">No payments recorded today</p>
              )}

              {/* Yesterday comparison */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className="text-sm text-gray-500">Yesterday&apos;s Total</span>
                <span className="font-medium text-gray-600">₹{paymentSummary.yesterday_total?.toLocaleString() || 0}</span>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">Unable to load payment summary</p>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-6">
          <PurchaseAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
}
